import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Rate, Typography, Progress, Row, Col, Skeleton, Avatar, Pagination, Tag, Drawer } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import { reviewActions } from '../actions/reviewActions';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const RatingsWidget: React.FC = () => {
  const dispatch = useDispatch();
  const schoolRatingsState = useSelector((state: any) => state.getSchoolRatingsReducer);

  const [selectedStar, setSelectedStar] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

 

  const handleStarClick = (star: number) => {
    const newStar = selectedStar === star ? null : star;
    setSelectedStar(newStar);
    setCurrentPage(1);
    setDrawerOpen(true);
    dispatch(reviewActions.getSchoolRatingsRequest({ starFilter: newStar ?? undefined, pageNumber: 1, pageSize }));
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedStar(null);
    dispatch(reviewActions.getSchoolRatingsRequest());
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch(reviewActions.getSchoolRatingsRequest({ starFilter: selectedStar ?? undefined, pageNumber: page, pageSize }));
  };

  const isLoading = schoolRatingsState?.isLoading;
  const data = schoolRatingsState?.data;

  const isAdminView = data && Array.isArray(data?.data);
  const summary = isAdminView ? data?.summary : data;
  const reviewList: any[] = isAdminView ? (data?.data || []) : [];
  const totalCount: number = isAdminView ? (data?.totalCount || 0) : 0;

  const avg = summary?.averageSchoolRating ?? 0;
  const total = summary?.totalReviews ?? 0;
  const starBreakdown = summary?.starBreakdown ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const ratingColorClass = avg >= 4 ? 'rating-color-good' : avg >= 3 ? 'rating-color-medium' : 'rating-color-poor';

  if (!isLoading && total === 0 && !drawerOpen) {
    return null;
  }

  if (isLoading && !drawerOpen) {
    return (
      <Card className="rating-widget-skeleton-card">
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  return (
    <>
      <Card className="rating-widget-card border-none" bodyStyle={{ padding: 20 }}>
        {total === 0 ? (
          <div className="rating-widget-empty">
            <StarOutlined className="rating-widget-empty-icon" />
            <div className="rating-widget-empty-text-wrap">
              <Text type="secondary" className="rating-widget-empty-text">No reviews yet</Text>
            </div>
          </div>
        ) : (
          <>
            <div className='rating-widget-header'>
              <div>
                <Title className="rating-widget-title" level={4}>Customer Ratings</Title>
                <Text className="rating-widget-subtitle" type="secondary">See what your customers think</Text>
              </div>
            </div>
            <div className='rating-wrap'>
              <div className="rating-widget-avg-row">
                <Title level={2} className={`rating-widget-avg-num ${ratingColorClass}`}>
                  {avg.toFixed(1)} <span className="rating-widget-avg-out-of">/ 5</span>
                </Title>
                <div>
                  <Rate disabled allowHalf value={avg} className="rating-widget-stars" />
                  <div className="rating-widget-review-count-wrap">
                    <Text type="secondary" className="rating-widget-review-count">
                      {total} {total === 1 ? 'review' : 'reviews'}
                    </Text>
                  </div>
                </div>
              </div>

              <div className="rating-widget-bar-wrap">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = starBreakdown[star] ?? 0;
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                  const strokeColor = star >= 4 ? '#52c41a' : star === 3 ? '#faad14' : '#ff4d4f';
                  const isActive = selectedStar === star;
                  const activeClass = isActive
                    ? star >= 4 ? 'bar-row-active-good' : star === 3 ? 'bar-row-active-medium' : 'bar-row-active-poor'
                    : '';
                  return (
                    <Row
                      key={star}
                      align="middle"
                      gutter={6}
                      className={`rating-widget-bar-row ${activeClass}`}
                      onClick={() => isAdminView !== false && handleStarClick(star)}
                    >
                      <Col className="rating-widget-star-label-col">
                        <Text className={`rating-widget-star-label ${isActive ? 'star-label-active' : ''}`}>
                          {star}★
                        </Text>
                      </Col>
                      <Col flex="auto">
                        <Progress
                          percent={percent}
                          showInfo={false}
                          strokeColor={strokeColor}
                          trailColor="#f5f5f5"
                          size="small"
                        />
                      </Col>
                      <Col className="rating-widget-count-col">
                        <Text type="secondary" className="rating-widget-count-text">{count}</Text>
                      </Col>
                    </Row>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </Card>

      <Drawer
        title={null}
        placement="right"
        width={500}
        open={drawerOpen}
        onClose={handleDrawerClose}
        bodyStyle={{ padding: 0, background: '#f8fafc' }}
        headerStyle={{ display: 'none' }}
      >
        {/* Drawer Header Banner */}
        <div className="drawer-header-banner">
          <button onClick={handleDrawerClose} className="drawer-close-btn">
            ✕
          </button>

          <div className="drawer-title-row">
            <div className="drawer-star-icon">
              <span className="drawer-single-star">
                ★
              </span>
            </div>
            <div>
              <Text className="drawer-title-text">
                {selectedStar ? `${selectedStar}-Star Reviews` : 'All Reviews'}
              </Text>
              <Text className="drawer-title-subtitle">
                School ratings from students
              </Text>
            </div>
          </div>

          <div className="drawer-stats-row">
            <div className="drawer-stat-box">
              <Text className="drawer-stat-value">{totalCount}</Text>
              <Text className="drawer-stat-label">
                {selectedStar ? `${selectedStar}★ reviews` : 'Total reviews'}
              </Text>
            </div>
            {selectedStar && (
              <div className="drawer-stat-box">
                <Text className="drawer-stat-value">{avg.toFixed(1)}</Text>
                <Text className="drawer-stat-label">Overall avg</Text>
              </div>
            )}
            <div className="drawer-stat-box">
              <Rate
                disabled
                value={selectedStar ?? avg}
                allowHalf
                className="drawer-stat-rate"
              />
              <Text className="drawer-stat-label drawer-stat-label-block">
                {selectedStar ? `${selectedStar} star` : 'avg rating'}
              </Text>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="drawer-body">
          {isLoading ? (
            <div className="drawer-skeleton-list">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="review-card" bodyStyle={{ padding: 16 }}>
                  <Skeleton avatar active paragraph={{ rows: 2 }} />
                </Card>
              ))}
            </div>
          ) : reviewList.length === 0 ? (
            <div className="drawer-empty">
              <div className="drawer-empty-icon">🔍</div>
              <Text strong className="drawer-empty-title">
                No {selectedStar}★ reviews yet
              </Text>
              <Text type="secondary" className="drawer-empty-subtitle">
                Students haven't left any {selectedStar}-star ratings
              </Text>
            </div>
          ) : (
            <div className="review-list">
              {reviewList.map((review: any, index: number) => {
                const ratingClass = review.rating >= 4 ? 'review-tag-good' : review.rating >= 3 ? 'review-tag-medium' : 'review-tag-poor';
                const avatarHue = (review.studentName?.charCodeAt(0) || 65) * 5 % 360;
                return (
                  <Card
                    key={review._id || index}
                    className="review-card"
                    bodyStyle={{ padding: '16px 18px' }}
                  >
                    <div className="review-top-row">
                      <Avatar
                        size={40}
                        className="review-avatar"
                        style={{ background: `hsl(${avatarHue}, 60%, 60%)` }}
                      >
                        {review.studentName?.charAt(0)?.toUpperCase() || 'A'}
                      </Avatar>

                      <div className="review-info">
                        <div className="review-name-row">
                          <Text strong className="review-name">
                            {review.studentName || 'Anonymous'}
                          </Text>
                          <Text type="secondary" className="review-time">
                            {dayjs(review.createdAt).fromNow()}
                          </Text>
                        </div>
                       
                        <div className="review-rating-row">
                          <Rate disabled value={review.rating} allowHalf className="review-stars" />
                          <Tag className={`review-tag ${ratingClass}`}>
                            {review.rating >= 4.5 ? 'Excellent' : review.rating >= 4 ? 'Very Good' : review.rating >= 3 ? 'Good' : review.rating >= 2 ? 'Fair' : 'Poor'}
                          </Tag>
                        </div>
                      </div>
                    </div>

                    {review.comment ? (
                      <div className="review-comment-box">
                        <Text className="review-comment-text">
                          "{review.comment}"
                        </Text>
                      </div>
                    ) : (
                      <div className="review-comment-box review-comment-empty">
                        <Text type="secondary" className="review-comment-no-text">
                          No comment provided
                        </Text>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {totalCount > pageSize && (
          <div className="drawer-pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={handlePageChange}
              size="small"
              showTotal={(t, range) => `${range[0]}–${range[1]} of ${t} reviews`}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default RatingsWidget;